import { useEffect, useMemo, useState } from "react";

export const MAX_PICKS = 10;

const isPublished = (post) => post?.status === "published";

const MegaprompterPicker = ({ posts = [], onStart, onPickedCountChange }) => {
  const [picked, setPicked] = useState([]); // ordered array of post ids
  const [tagFilter, setTagFilter] = useState("");
  const [columnFilter, setColumnFilter] = useState(null); // board column id
  const [showFilters, setShowFilters] = useState(false);

  // The board columns represented in the incoming posts, in board order. The
  // caller tags every post with __columnId/__columnName; anything untagged
  // lands in a single "Other" bucket so older call sites still work.
  const columnOptions = useMemo(() => {
    const seen = new Map();
    for (const post of posts) {
      if (!post || !post.text) continue;
      const id = post.__columnId ?? "other";
      if (!seen.has(id)) {
        seen.set(id, { id, name: post.__columnName ?? "Other", count: 0 });
      }
      seen.get(id).count += 1;
    }
    return Array.from(seen.values());
  }, [posts]);

  // Exactly one column is shown at a time. Nothing is selected on first render
  // and a column can disappear between renders (a post moved, a filter changed),
  // so both cases fall back to the first column the board handed us.
  const defaultColumn = columnOptions[0]?.id ?? null;
  const activeColumn = columnOptions.some((c) => c.id === columnFilter)
    ? columnFilter
    : defaultColumn;

  const groups = useMemo(() => {
    const query = tagFilter.trim().toLowerCase();
    const matches = (post) => {
      if (!query) return true;
      const searchable = [
        ...(post?.tags ?? []).map((t) => t.name?.toLowerCase()),
        post?.post_type?.toLowerCase(),
        post?.content_type?.toLowerCase(),
        post?.content_pillar?.pillar?.toLowerCase(),
      ].filter(Boolean);
      return searchable.some((s) => s.includes(query));
    };

    // Buckets by group name → { published: [], unpublished: [] }
    const byGroup = new Map();
    for (const post of posts) {
      if (!post || !post.text) continue; // can't prompt empty posts
      if ((post.__columnId ?? "other") !== activeColumn) continue;
      if (!matches(post)) continue;
      const groupName = post.__groupName || post.groupName || post.group?.name || "Posts";
      if (!byGroup.has(groupName)) {
        byGroup.set(groupName, { published: [], unpublished: [] });
      }
      const bucket = byGroup.get(groupName);
      (isPublished(post) ? bucket.published : bucket.unpublished).push(post);
    }
    return Array.from(byGroup.entries()).map(([name, b]) => ({ name, ...b }));
  }, [posts, tagFilter, activeColumn]);

  const togglePick = (id) => {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((p) => p !== id);
      if (prev.length >= MAX_PICKS) return prev;
      return [...prev, id];
    });
  };

  // The modal shows the running count next to its title, so keep it in sync.
  useEffect(() => {
    onPickedCountChange && onPickedCountChange(picked.length);
  }, [picked.length, onPickedCountChange]);

  const orderFor = (id) => picked.indexOf(id) + 1; // 0 means not picked

  const handleStart = () => {
    if (picked.length === 0) return;
    const postsById = new Map(posts.map((p) => [p.id, p]));
    const ordered = picked.map((id) => postsById.get(id)).filter(Boolean);
    onStart(ordered);
  };

  const renderRow = (post) => {
    const order = orderFor(post.id);
    const checked = order > 0;
    const disabled = !checked && picked.length >= MAX_PICKS;
    return (
      <div
        key={post.id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-pressed={checked}
        onClick={() => !disabled && togglePick(post.id)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            togglePick(post.id);
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
          borderRadius: 8,
          cursor: disabled ? "not-allowed" : "pointer",
          background: checked ? "rgba(3,142,220,0.08)" : "transparent",
          border: `1px solid ${checked ? "#038edc" : "rgba(0,0,0,0.08)"}`,
          marginBottom: 6,
          opacity: disabled ? 0.45 : 1,
        }}
      >
        {/* Always rendered so titles stay aligned whether or not a row is picked. */}
        <span
          style={{
            minWidth: 28,
            height: 28,
            borderRadius: "50%",
            background: checked ? "#038edc" : "transparent",
            border: checked ? "none" : "1.5px solid #d8dce3",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          {checked ? order : ""}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {post.title || `Post #${post.id}`}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {(post.text || "").slice(0, 110).replace(/\s+/g, " ")}
          </div>
        </div>
      </div>
    );
  };

  const sectionHeader = (text, count, right = null) => (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 8, margin: "12px 0 6px", minHeight: 20,
    }}>
      <div style={{
        display: "flex", alignItems: "center",
        fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
        color: "#038edc", lineHeight: 1,
      }}>
        {text}
        {count > 0 && (
          <span style={{ color: "#6b7280", fontWeight: 600, marginLeft: 5 }}>({count})</span>
        )}
      </div>
      {right}
    </div>
  );

  // The posts actually on screen, in the order they are rendered.
  const displayedPosts = groups.flatMap((g) => [...g.unpublished, ...g.published]);
  const totalEligible = displayedPosts.length;

  // Marking everything only makes sense while the whole list fits under the cap.
  const canMarkAll = totalEligible > 0 && totalEligible <= MAX_PICKS;
  const allDisplayedPicked =
    totalEligible > 0 && displayedPosts.every((p) => picked.includes(p.id));

  const toggleMarkAll = () => {
    const displayedIds = displayedPosts.map((p) => p.id);
    if (allDisplayedPicked) {
      const drop = new Set(displayedIds);
      setPicked((prev) => prev.filter((id) => !drop.has(id)));
      return;
    }
    // Append the missing ones, keeping the order already chosen.
    setPicked((prev) => {
      const next = [...prev];
      for (const id of displayedIds) {
        if (next.length >= MAX_PICKS) break;
        if (!next.includes(id)) next.push(id);
      }
      return next;
    });
  };

  const markAllControl = canMarkAll ? (
    <label
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
        fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "none", letterSpacing: 0,
        // Matches the heading's line box so both sides sit on the same baseline.
        lineHeight: 1, margin: 0,
      }}
    >
      <input
        type="checkbox"
        checked={allDisplayedPicked}
        onChange={toggleMarkAll}
        // The UA default margin on a checkbox is what pushes this off the heading's line.
        style={{ accentColor: "#038edc", width: 15, height: 15, margin: 0, display: "block" }}
      />
      {allDisplayedPicked ? "Unmark all" : `Mark all (${totalEligible})`}
    </label>
  ) : null;

  const activeColumnName = columnOptions.find((c) => c.id === activeColumn)?.name;
  // A column is always selected, so only the tag counts as a filter being on.
  const activeFilterCount = tagFilter.trim() ? 1 : 0;
  // The column is already called out as the section heading, so the collapsed
  // summary only needs to surface the tag filter.
  const activeFilterSummary = tagFilter.trim() ? `Tag: "${tagFilter.trim()}"` : "";
  // Reset also drops the picks and returns to the first column, so it stays live
  // whenever any of those three is away from its starting state.
  const canReset =
    activeFilterCount > 0 || picked.length > 0 || activeColumn !== defaultColumn;
  // With a column selected its name says more than a generic "Unpublished";
  // across all columns the bucket really is just everything not yet published.
  const unpublishedHeading = activeColumnName ?? "Unpublished";
  // "Posts" is the generic fallback the boards send when a post has no content
  // type - it labels nothing, so it is not worth a heading.
  const groupHeading = (name) => (name && name !== "Posts" ? name : null);
  const startDisabled = picked.length === 0;

  // Changing what is on screen invalidates the selection, so both of these
  // clear the picks rather than leaving posts ticked that are no longer visible.
  const resetFilters = () => {
    setColumnFilter(defaultColumn);
    setTagFilter("");
    setPicked([]);
  };

  const changeColumn = (columnId) => {
    setColumnFilter(columnId);
    setPicked([]);
  };

  return (
    <div>
      {/* Start + filter toggle read as one segmented control. */}
      <div style={{ display: "flex", marginBottom: showFilters || (!showFilters && activeFilterSummary) ? 10 : 16 }}>
        <button
          onClick={handleStart}
          disabled={startDisabled}
          style={{
            flex: 1,
            padding: "12px 16px", border: "none", borderRadius: "8px 0 0 8px",
            background: startDisabled ? "#9ca3af" : "#038edc",
            color: "#fff", cursor: startDisabled ? "not-allowed" : "pointer",
            fontWeight: 700, fontSize: 14,
          }}
        >
          Start Megaprompter
        </button>
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          title="Filters"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0,
            padding: "12px 16px", border: "none", borderRadius: "0 8px 8px 0",
            borderLeft: "1px solid rgba(255,255,255,0.28)",
            // A shade darker than the start half, so the pair still reads as one
            // button while the toggle stays obviously pressable.
            background: startDisabled ? "#6b7280" : "#0277a3",
            color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 14,
          }}
        >
          <i className="uil uil-filter" />
          {activeFilterCount > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, minWidth: 16, height: 16, borderRadius: 8,
              background: "#fff", color: "#0277a3",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}>
              {activeFilterCount}
            </span>
          )}
          <i className={`uil ${showFilters ? "uil-angle-up" : "uil-angle-down"}`} />
        </button>
      </div>

      {/* Collapsed but filtering is easy to miss, so name what is active. */}
      {!showFilters && activeFilterSummary && (
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {activeFilterSummary}
        </div>
      )}

      {showFilters && columnOptions.length > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, flexShrink: 0 }}>Column:</span>
          <select
            value={activeColumn}
            onChange={(e) => changeColumn(e.target.value)}
            style={{ fontSize: 12, padding: "5px 10px", borderRadius: 8, border: "1.5px solid #e0e4ea", outline: "none", background: "#fff", color: "#374151", fontWeight: 600, cursor: "pointer" }}
          >
            {columnOptions.map((col) => (
              <option key={col.id} value={col.id}>
                {col.name} ({col.count})
              </option>
            ))}
          </select>
        </div>
      )}

      {showFilters && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, flexShrink: 0 }}>Filter by tag:</span>
          <input
            type="text"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            placeholder="e.g. Personal, Video, published…"
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 8, border: "1.5px solid #e0e4ea", outline: "none", width: 220 }}
          />
          {tagFilter && (
            <button
              type="button"
              onClick={() => setTagFilter("")}
              style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 8, border: "1.5px solid #e0e4ea", background: "transparent", color: "#6b7280", cursor: "pointer" }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {showFilters && (
        <div style={{ marginBottom: 16 }}>
          <button
            type="button"
            onClick={resetFilters}
            disabled={!canReset}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 8,
              border: `1.5px solid ${canReset ? "#f34e4e" : "#e0e4ea"}`,
              background: canReset ? "rgba(243,78,78,0.08)" : "transparent",
              color: canReset ? "#f34e4e" : "#c3c8d0",
              cursor: canReset ? "pointer" : "default",
            }}
          >
            <i className="uil uil-redo" />
            Reset filters
          </button>
        </div>
      )}

      {totalEligible === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
          {tagFilter
            ? `No posts match "${tagFilter}" in this column.`
            : "No posts with content in this column."}
        </div>
      ) : (
        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {groups.map((g, groupIndex) => {
            // Mark all covers the whole visible list, so it belongs on the very
            // first section header - whichever of the two that turns out to be.
            const first = groupIndex === 0;
            const onUnpublished = first && g.unpublished.length > 0;
            const onPublished = first && g.unpublished.length === 0;
            return (
              <div key={g.name} style={{ marginBottom: 16 }}>
                {groups.length > 1 && groupHeading(g.name) && (
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
                    {groupHeading(g.name)}
                  </div>
                )}
                {g.unpublished.length > 0 && (
                  <>
                    {sectionHeader(unpublishedHeading, g.unpublished.length, onUnpublished ? markAllControl : null)}
                    {g.unpublished.map(renderRow)}
                  </>
                )}
                {g.published.length > 0 && (
                  <>
                    {sectionHeader("Published", g.published.length, onPublished ? markAllControl : null)}
                    {g.published.map(renderRow)}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MegaprompterPicker;
